import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Grid } from '@/UI/Grid/Grid';
import NavigationDrawer from '@/components/Dashboard/NavigationDrawer';
import { DashboardMain } from '@/components/Dashboard/DashboardMain';
import { I18N_KEYS } from '@/language/keys';
import { DashboardHeader } from '@/components/DashboardHeader/DashboardHeader';
import { useDashboardFolders } from '@/components/Dashboard/useDashboardFolders';
import { useWorkspaceFolders } from '@/components/Dashboard/useWorkspaceFolders';
import { Loader } from '@/components/Dashboard/Loader';
import { FlexCol } from '@/UI/styles';
import { log } from '@/logger/logger';
import { SubscriptionType } from '@/types/shared';
import { DashboardSection } from '@/components/Dashboard/enums/shared';
import { useDashboardSection } from '@/components/Dashboard/hooks/useDashboardSection';
import { getDashboardSectionFromScope } from '@/components/Dashboard/utils/dashboardSection.utils';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { ReloadAlert } from '@/UI/ReloadAlert/ReloadAlert';
import { WelcomeModal } from '@/components/WelcomeModal/WelcomeModal';
import DashboardArchive from '@/components/Dashboard/Archive/ArchiveTab';
import { useGetArchiveItems } from '@/queries/useArchive';
import { useGetUserSharedApps, useGetPublicRecipes, useCreateRecipe } from '@/queries/useRecipes';
import { FolderScope } from '@/enums/folder-scope.enum';
import type { User } from '@/types/auth.types';

const logger = log.getLogger('Dashboard');

const DRAWER_WIDTH = 240;

interface DashboardLocationState {
  dashboardSection?: DashboardSection;
}

interface DashboardProps {
  user: User;
}

export function Dashboard({ user }: DashboardProps) {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { folderId } = useParams<{ folderId?: string }>();
  const location = useLocation();
  const locationState = useMemo(() => (location.state ?? {}) as DashboardLocationState, [location.state]);
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [, setIsClosing] = useState(false);

  const { mutateAsync: createRecipe } = useCreateRecipe();

  const {
    data: { sharedApps = [], sharedWithCurrentUser = [] } = {},
    isError: sharedAppsError,
    refetch: refetchSharedApps,
  } = useGetUserSharedApps();
  const { data: showCaseRecipes = [] } = useGetPublicRecipes();

  const {
    recipes: personalRecipes,
    folders: personalFolders,
    navigateToFolder: navigateToPersonalFolder,
    currentFolderId: currentPersonalFolderId,
    folderPath: personalFolderPath,
    isLoading: personalFoldersLoading,
    isError: personalFoldersError,
    refetch: refetchPersonalFolders,
  } = useDashboardFolders();

  const {
    recipes: workspaceRecipes,
    folders: workspaceFolders,
    navigateToFolder: navigateToWorkspaceFolder,
    currentFolderId: currentWorkspaceFolderId,
    folderPath: workspaceFolderPath,
    isLoading: workspaceFoldersLoading,
    isError: workspaceFoldersError,
    refetch: refetchWorkspaceFolders,
  } = useWorkspaceFolders();

  const { currentSelection, setCurrentSelection } = useDashboardSection();
  const { isLoading: archiveLoading } = useGetArchiveItems(currentSelection === DashboardSection.Archive);

  useEffect(() => {
    const { dashboardSection: stateSection } = locationState;

    if (folderId) {
      const isInWorkspaceFolders = workspaceFolders.some((f) => f.id === folderId);
      const isInPersonalFolders = personalFolders.some((f) => f.id === folderId);

      if (isInWorkspaceFolders) {
        navigateToWorkspaceFolder(folderId);
        setCurrentSelection(DashboardSection.WorkspaceFiles);
      } else if (isInPersonalFolders) {
        navigateToPersonalFolder(folderId);
        setCurrentSelection(DashboardSection.RecentRecipes);
      }
    } else {
      navigateToPersonalFolder(null);
      navigateToWorkspaceFolder(null);

      // Root-level recipes have no folderId, so we rely on the scope passed via
      // router state to select the correct dashboard section (e.g. Workspace Files).
      if (stateSection) {
        setCurrentSelection(stateSection);
      }
    }

    if (stateSection) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [
    folderId,
    location.pathname,
    locationState,
    navigate,
    navigateToPersonalFolder,
    navigateToWorkspaceFolder,
    setCurrentSelection,
    workspaceFolders,
    personalFolders,
  ]);

  const navigateToDashboardScope = useCallback(
    (scope: FolderScope, targetFolderId?: string | null) => {
      setCurrentSelection(getDashboardSectionFromScope(scope));
      navigate(targetFolderId ? `/folders/${targetFolderId}` : '/');
    },
    [navigate, setCurrentSelection],
  );

  const handleFolderNavigate = useCallback(
    (targetFolderId?: string | null) => {
      navigateToDashboardScope(FolderScope.PERSONAL, targetFolderId);
    },
    [navigateToDashboardScope],
  );

  const handleWorkspaceFolderNavigate = useCallback(
    (targetFolderId?: string | null) => {
      navigateToDashboardScope(FolderScope.WORKSPACE, targetFolderId);
    },
    [navigateToDashboardScope],
  );

  const handleCreateRecipe = useCallback(async () => {
    track('created_new_flow', { source: 'dashboard' }, TrackTypeEnum.BI);
    try {
      await createRecipe();
    } catch (error) {
      logger.error('error creating recipe', error);
    }
  }, [createRecipe, track]);

  const hasTeamOrEnterpriseSubscription = useMemo(() => {
    return (
      user.activeWorkspace.subscription.type === SubscriptionType.Team ||
      user.activeWorkspace.subscription.type === SubscriptionType.Enterprise
    );
  }, [user.activeWorkspace.subscription.type]);

  const handleSectionSelectionChange = useCallback(
    (selection: DashboardSection) => {
      setCurrentSelection(selection);
      // Clear the folder ID from URL when changing sections
      navigate('/');

      // Also clear the folder navigation state
      if (currentPersonalFolderId) {
        void navigateToPersonalFolder(null);
      }
      if (currentWorkspaceFolderId) {
        void navigateToWorkspaceFolder(null);
      }
    },
    [
      navigate,
      currentPersonalFolderId,
      currentWorkspaceFolderId,
      navigateToPersonalFolder,
      navigateToWorkspaceFolder,
      setCurrentSelection,
    ],
  );

  const handleReloadPersonalFolders = useCallback(() => {
    void refetchPersonalFolders();
  }, [refetchPersonalFolders]);

  const handleReloadSharedApps = useCallback(() => {
    void refetchSharedApps();
  }, [refetchSharedApps]);

  const handleReloadWorkspaceFolders = useCallback(() => {
    void refetchWorkspaceFolders();
  }, [refetchWorkspaceFolders]);

  const dashboardSections = useMemo(
    () => ({
      [DashboardSection.RecentRecipes]: (
        <DashboardMain
          recipes={personalRecipes}
          title={t(I18N_KEYS.DASHBOARD.PAGES.FILES.TITLE)}
          showCase={showCaseRecipes}
          section={DashboardSection.RecentRecipes}
          showChangeNameButton={hasTeamOrEnterpriseSubscription}
          onFolderNavigate={handleFolderNavigate}
          currentFolderId={currentPersonalFolderId}
          folders={personalFolders}
          folderPath={personalFolderPath}
          isError={personalFoldersError}
          onReload={handleReloadPersonalFolders}
        />
      ),
      [DashboardSection.SharedWithMe]: (
        <DashboardMain
          recipes={sharedWithCurrentUser}
          title={t(I18N_KEYS.DASHBOARD.PAGES.SHARED_WITH_ME.TITLE)}
          showCase={showCaseRecipes}
          section={DashboardSection.SharedWithMe}
          onSectionChange={handleSectionSelectionChange}
          isError={sharedAppsError}
          onReload={handleReloadSharedApps}
        />
      ),
      [DashboardSection.WorkspaceFiles]: (
        <DashboardMain
          recipes={workspaceRecipes}
          title={t(I18N_KEYS.DASHBOARD.PAGES.WORKSPACE_FILES.TITLE)}
          showCase={showCaseRecipes}
          section={DashboardSection.WorkspaceFiles}
          onSectionChange={handleSectionSelectionChange}
          onFolderNavigate={handleWorkspaceFolderNavigate}
          currentFolderId={currentWorkspaceFolderId}
          folders={workspaceFolders}
          folderPath={workspaceFolderPath}
          isError={workspaceFoldersError}
          onReload={handleReloadWorkspaceFolders}
        />
      ),
      [DashboardSection.SharedApps]: (
        <DashboardMain
          recipes={sharedApps}
          title={t(I18N_KEYS.DASHBOARD.PAGES.SHARED_APPS.TITLE)}
          showCase={showCaseRecipes}
          section={DashboardSection.SharedApps}
          isError={sharedAppsError}
          onReload={handleReloadSharedApps}
        />
      ),
      [DashboardSection.Archive]: <DashboardArchive />,
    }),
    [
      personalRecipes,
      sharedApps,
      showCaseRecipes,
      t,
      workspaceRecipes,
      sharedWithCurrentUser,
      handleFolderNavigate,
      handleWorkspaceFolderNavigate,
      hasTeamOrEnterpriseSubscription,
      handleSectionSelectionChange,
      currentPersonalFolderId,
      currentWorkspaceFolderId,
      personalFolders,
      personalFolderPath,
      personalFoldersError,
      handleReloadPersonalFolders,
      workspaceFolders,
      workspaceFolderPath,
      workspaceFoldersError,
      handleReloadWorkspaceFolders,
      sharedAppsError,
      handleReloadSharedApps,
    ],
  );

  return (
    <>
      <Grid container sx={{ position: 'relative', height: '100%' }} data-testid="dashboard-container">
        <Grid size="auto">
          <NavigationDrawer
            createRecipe={handleCreateRecipe}
            hasSharedRecipes={sharedWithCurrentUser.length > 0}
            hasWorkspaceFiles={workspaceRecipes.length > 0 || workspaceFolders.length > 0}
            showWorkspaceFilesSection={hasTeamOrEnterpriseSubscription}
            drawerWidth={DRAWER_WIDTH}
            mobileOpen={mobileOpen}
            onSectionChange={handleSectionSelectionChange}
            selectedSection={currentSelection}
            setIsClosing={setIsClosing}
            setMobileOpen={setMobileOpen}
            user={user}
          />
        </Grid>

        <Grid size="grow">
          <FlexCol sx={{ height: '100%' }}>
            <FlexCol sx={{ pt: 4, px: 8, gap: currentSelection === DashboardSection.Archive ? 0 : 4, height: '100%' }}>
              <DashboardHeader
                createRecipe={handleCreateRecipe}
                isShowCreateRecipeButton={currentSelection === DashboardSection.RecentRecipes}
              />
              {dashboardSections[currentSelection]}
            </FlexCol>
          </FlexCol>
        </Grid>
      </Grid>
      {!personalFoldersLoading &&
      (currentSelection !== DashboardSection.WorkspaceFiles || !workspaceFoldersLoading) &&
      (currentSelection !== DashboardSection.Archive || !archiveLoading) ? null : (
        <Loader offset={DRAWER_WIDTH} />
      )}
      <ReloadAlert />
      <WelcomeModal />
    </>
  );
}
